import React, { useState, useEffect, useCallback } from 'react';
import { 
  Upload, Image, Trash2, Edit2, Check, X, FolderOpen,
  Search, Filter, Grid, List, Loader2, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMediaLibrary() {
  const [media, setMedia] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    pages: []
  });
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    fetchMedia();
    fetchPages();
  }, [pageFilter]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = pageFilter ? `?page_filter=${pageFilter}` : '';
      const response = await axios.get(`${API}/media/library${params}`);
      setMedia(response.data.media || []);
    } catch (error) {
      toast.error('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/media/pages`);
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setUploadForm({ ...uploadForm, file, title: file.name.split('.')[0] });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('pages', uploadForm.pages.join(','));

      await axios.post(`${API}/media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Image uploaded successfully!');
      setShowUploadForm(false);
      setUploadForm({ file: null, title: '', description: '', pages: [] });
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateMedia = async () => {
    if (!editingMedia) return;

    try {
      await axios.put(`${API}/media/${editingMedia.media_id}`, {
        title: editingMedia.title,
        description: editingMedia.description,
        pages: editingMedia.pages
      });

      toast.success('Media updated successfully');
      setEditingMedia(null);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to update media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/media/${mediaId}`);
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const togglePageAssignment = (pageId) => {
    if (editingMedia) {
      const newPages = editingMedia.pages.includes(pageId)
        ? editingMedia.pages.filter(p => p !== pageId)
        : [...editingMedia.pages, pageId];
      setEditingMedia({ ...editingMedia, pages: newPages });
    } else if (showUploadForm) {
      const newPages = uploadForm.pages.includes(pageId)
        ? uploadForm.pages.filter(p => p !== pageId)
        : [...uploadForm.pages, pageId];
      setUploadForm({ ...uploadForm, pages: newPages });
    }
  };

  const filteredMedia = media.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-media-library">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-charcoal">Media Library</h1>
          <p className="text-charcoal/60">Upload and manage images for your website pages</p>
        </div>
        <Button
          onClick={() => setShowUploadForm(true)}
          className="bg-green-700 hover:bg-green-800 text-white"
          data-testid="upload-media-btn"
        >
          <Upload size={18} className="mr-2" />
          Upload Image
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gold/10 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <Input
            placeholder="Search images..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-cream"
          />
        </div>
        <select
          value={pageFilter}
          onChange={(e) => setPageFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-gold/20 bg-cream text-charcoal"
        >
          <option value="">All Pages</option>
          {pages.map(page => (
            <option key={page.id} value={page.id}>{page.name}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-green-100 text-green-700' : 'bg-cream text-charcoal/60'}`}
          >
            <Grid size={20} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-green-100 text-green-700' : 'bg-cream text-charcoal/60'}`}
          >
            <List size={20} />
          </button>
        </div>
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-serif text-charcoal mb-4">Upload New Image</h2>
            
            <form onSubmit={handleUpload} className="space-y-4">
              {/* File Drop Zone */}
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  uploadForm.file ? 'border-green-400 bg-green-50' : 'border-gold/30 hover:border-gold/50'
                }`}
              >
                {uploadForm.file ? (
                  <div className="space-y-2">
                    <CheckCircle size={40} className="mx-auto text-green-600" />
                    <p className="font-medium text-charcoal">{uploadForm.file.name}</p>
                    <p className="text-sm text-charcoal/60">
                      {(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, file: null })}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Image size={40} className="mx-auto text-charcoal/30 mb-2" />
                    <p className="text-charcoal/70">Click to select an image</p>
                    <p className="text-xs text-charcoal/50 mt-1">JPEG, PNG, GIF, WebP (max 10MB)</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <Label>Title *</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  required
                  className="bg-cream"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  rows={2}
                  className="bg-cream"
                />
              </div>

              {/* Page Assignment */}
              <div>
                <Label>Assign to Pages</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {pages.map(page => (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => togglePageAssignment(page.id)}
                      className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        uploadForm.pages.includes(page.id)
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-cream text-charcoal/70 hover:bg-gold/10'
                      }`}
                    >
                      {uploadForm.pages.includes(page.id) && <Check size={14} className="inline mr-1" />}
                      {page.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowUploadForm(false);
                    setUploadForm({ file: null, title: '', description: '', pages: [] });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={uploading || !uploadForm.file}
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingMedia && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-serif text-charcoal mb-4">Edit Image</h2>
            
            <div className="space-y-4">
              {/* Preview */}
              <img 
                src={editingMedia.url} 
                alt={editingMedia.title}
                className="w-full h-40 object-cover rounded-lg"
              />

              {/* Title */}
              <div>
                <Label>Title</Label>
                <Input
                  value={editingMedia.title}
                  onChange={(e) => setEditingMedia({ ...editingMedia, title: e.target.value })}
                  className="bg-cream"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingMedia.description || ''}
                  onChange={(e) => setEditingMedia({ ...editingMedia, description: e.target.value })}
                  rows={2}
                  className="bg-cream"
                />
              </div>

              {/* Page Assignment */}
              <div>
                <Label>Assign to Pages</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {pages.map(page => (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => togglePageAssignment(page.id)}
                      className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        editingMedia.pages.includes(page.id)
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-cream text-charcoal/70 hover:bg-gold/10'
                      }`}
                    >
                      {editingMedia.pages.includes(page.id) && <Check size={14} className="inline mr-1" />}
                      {page.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingMedia(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateMedia}
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-green-700" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gold/10">
          <FolderOpen size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/60">No images found</p>
          <Button
            onClick={() => setShowUploadForm(true)}
            variant="outline"
            className="mt-4"
          >
            Upload your first image
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredMedia.map(item => (
            <div 
              key={item.media_id}
              className="bg-white rounded-xl overflow-hidden border border-gold/10 hover:shadow-lg transition-shadow group"
            >
              <div className="relative aspect-square">
                <img 
                  src={item.url} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => setEditingMedia(item)}
                    className="p-2 bg-white rounded-full hover:bg-green-50"
                  >
                    <Edit2 size={18} className="text-green-700" />
                  </button>
                  <button
                    onClick={() => handleDeleteMedia(item.media_id)}
                    className="p-2 bg-white rounded-full hover:bg-red-50"
                  >
                    <Trash2 size={18} className="text-red-600" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium text-charcoal text-sm truncate">{item.title}</h3>
                {item.pages.length > 0 && (
                  <p className="text-xs text-charcoal/50 mt-1">
                    {item.pages.length} page{item.pages.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gold/10 divide-y divide-gold/10">
          {filteredMedia.map(item => (
            <div 
              key={item.media_id}
              className="flex items-center gap-4 p-4 hover:bg-cream/50 transition-colors"
            >
              <img 
                src={item.url} 
                alt={item.title}
                className="w-16 h-16 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-charcoal">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-charcoal/60 truncate">{item.description}</p>
                )}
                <div className="flex gap-1 mt-1 flex-wrap">
                  {item.pages.map(pageId => {
                    const page = pages.find(p => p.id === pageId);
                    return page ? (
                      <span key={pageId} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {page.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingMedia(item)}
                  className="p-2 hover:bg-green-50 rounded-lg"
                >
                  <Edit2 size={18} className="text-green-700" />
                </button>
                <button
                  onClick={() => handleDeleteMedia(item.media_id)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
